import React, { useEffect, useState } from 'react';
import ChatBot, { Button, Settings, useMessages, ChatBotProvider, Styles } from 'react-chatbotify';
import { useTranslation } from 'react-i18next';
import LlmConnector, { LlmConnectorBlock } from '@rcb-plugins/llm-connector';
import CaRTBotAgent from '@/assets/images/cart_bot_agent.png';
import { ForgetMemoryBotButton } from '@/components/ChatBotAI/Buttons/ForgetMemoryBotButton';
import { SendScreenshotButton } from '@/components/ChatBotAI/Buttons/SendScreenshotButton';
import { SetWidthChatButton } from '@/components/ChatBotAI/Buttons/SetWidthChatButton';
import { cartIntelligenceEndPoint } from '@/services/serviceConstants';
import { GREY_THEME_SETTINGS, GREY_THEME_STYLES } from './BotThemes/GreyTheme';
import { FileButton } from './Buttons/FileButton';
import { ChatBotAIProvider } from './ChatBotAIProvider';
import DraggableChatButton from './DraggableChatButton';
import styles from './ChatBotAI.module.scss';

export const botAiProvider = new ChatBotAIProvider(cartIntelligenceEndPoint);

function ChatBotBridge() {
  const { injectMessage } = useMessages();

  useEffect(() => {
    botAiProvider.setInjectors(injectMessage);
  }, [injectMessage]);

  return null;
}

type ChatBotAIProps = { userId: string };

const ChatBotAI = ({ userId }: ChatBotAIProps) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isHalfScreen, setIsHalfScreen] = useState(botAiProvider.isWindowHalfScreen);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    botAiProvider.onWindowSizeChanged(() => {
      setIsHalfScreen(botAiProvider.isWindowHalfScreen);
    });
    botAiProvider.setFilesSelected(pendingFiles);
    botAiProvider.setClearAttachmentsCb(() => setPendingFiles([]));
  }, [pendingFiles]);

  useEffect(() => {
    botAiProvider.setTranslator(t);
  }, [t, i18n.language]);

  const botStyles: Styles = {
    ...GREY_THEME_STYLES,
    chatButtonStyle: {
      ...(GREY_THEME_STYLES.chatButtonStyle || {}),
      display: 'none',
    },
    footerStyle: {
      ...GREY_THEME_STYLES.footerStyle,
      maxHeight: 'none',
      height: 'auto',
      position: 'relative',
      paddingBottom: '42px',
    },
    chatWindowStyle: {
      ...GREY_THEME_STYLES.chatWindowStyle,
      ...(isHalfScreen ? { height: 'calc(100vh - 85px)', width: 'calc(55vw - 20px)' } : {}),
    },
  };

  const botSettings: Settings = {
    ...GREY_THEME_SETTINGS,
    chatButton: { icon: CaRTBotAgent },
    botBubble: { simulateStream: true, animate: false, streamSpeed: 15 },
    header: {
      title: 'CaRT Assistant',
      avatar: CaRTBotAgent,
      buttons: [<SetWidthChatButton key={2} />, Button.CLOSE_CHAT_BUTTON],
      showAvatar: true,
    },
    footer: {
      text: (
        <span className={styles.CreditsLabel}>
          {t('cartAI.poweredBy')}<br />{t('cartAI.disclaimer')}
        </span>
      ),
      buttons: [<FileButton key="files" />, <SendScreenshotButton key={2} />, <ForgetMemoryBotButton key={3} />],
    },
    tooltip: { text: t('cartAI.callToAction') as string, mode: 'START' },
    chatHistory: { storageKey: `cart_chat_history_${userId}` },
    chatInput: {
      showCharacterCount: true,
      enabledPlaceholderText: t('cartAI.placeholderMainInput') as string,
      buttons: [
        Button.SEND_MESSAGE_BUTTON,
        // <RecorderButton key={2} useFloat32 /> Hidden as requested in https://fst-jira.dot.app.corpintra.net/browse/CART-17702
      ],
      botDelay: 700,
    },
    voice: { disabled: false, sendAsAudio: true },
    audio: { disabled: true },
    fileAttachment: { disabled: true },
  };

  const flow = {
    start: { message: t('cartAI.firstMessage'), transition: 0, path: 'llm_block' },
    llm_block: {
      llmConnector: {
        provider: botAiProvider,
        errorMessage: t('cartAI.errorMessage'),
      },
    } as LlmConnectorBlock,
  };

  return (
    <div className={`${styles.ChatBotContainer} ${isHalfScreen ? styles.HalfScreen : ''}`} id="window-chatbot">
      <ChatBotProvider>
        <ChatBotBridge />
        <ChatBot plugins={[LlmConnector()]} flow={flow} settings={botSettings} styles={botStyles} />
        <DraggableChatButton userId={userId} />
      </ChatBotProvider>
    </div>
  );
};

export default ChatBotAI;
